// A new category (?newCategory=cucina|bevande): the shared drawer on just a name, which creates the category when it
// loses focus, as "Aggiungi piatto" does for an item. The tab sets food or drink; the drawer closes once it exists.
import { useRef, useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { saveError } from "../format";
import type { MenuMutations } from "../hooks/useMenu";
import type { MenuCategory } from "../types";
import { NameStep } from "./drawerFields";

interface Props {
  /** The tab the category goes in, while the drawer asks for its name; null when shut. */
  tab: "cucina" | "bevande" | null;
  mutations: MenuMutations;
  onCreated: (category: MenuCategory) => void;
  onClose: () => void;
}

export function NewCategoryDrawer({ tab, mutations, onCreated, onClose }: Props) {
  const [error, setError] = useState<string | null>(null);
  // The header keeps its tab while the drawer slides out.
  const shownTab = useRef(tab);
  if (tab !== null) shownTab.current = tab;
  const where = shownTab.current === "bevande" ? "Bevande" : "Cucina";
  const create = (name: string) => {
    setError(null);
    mutations.createCategory.mutate({ name, isDrink: tab === "bevande" }, {
      onSuccess: (answer) => onCreated(answer.category),
      onError: (e) => setError(saveError(e)),
    });
  };
  const close = () => {
    setError(null);
    onClose();
  };
  return (
    <Drawer
      isOpen={tab !== null}
      onClose={close}
      header={<p className="flex min-h-[24px] items-center text-[13px] font-medium text-gray-500">Nuova categoria in {where}</p>}
      bodyPadding
      title="Nuova categoria"
      a11yDescription="Il nome della nuova categoria: si crea quando lasci il campo."
      className="md:max-w-lg"
      // The name input takes the focus itself (autoFocus), which Radix then leaves where it is.
      onOpenAutoFocus={(event) => event.preventDefault()}
    >
      {tab !== null && (
        <NameStep
          key={tab}
          busy={mutations.createCategory.isPending}
          error={error}
          hint="Basta il nome: le voci si aggiungono dopo, in fondo alla categoria."
          onName={create}
        />
      )}
    </Drawer>
  );
}
